import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AppState } from '../../store/state';
import { AppDispatch } from '../../store/store';
import { Submission } from '../../store/submission/types';
import submissionSlice from '../../store/submission/slice';
import {
  selectBidState,
  selectInfo,
  selectKeywordState,
  selectSubmissionState,
  selectTrackRoles,
} from '../../store/selectors';
import { connect } from 'react-redux';
import Loading from '../../components/Loading/Loading';
import { ChairInfo, Info, InfoDeleteDTO, TableFriendlyName } from '../../store/info/types';
import { formatKeywordColumn } from '../../helpers/table';
import MyGridRedux from '../../components/MyGridRedux/MyGridRedux';
import { useTranslation } from 'react-i18next';
import TableHeader from '../../components/TableHeader/TableHeader';
import { Role } from '../../store/conference/types';
import infoSlice from '../../store/info/slice';
import { getRouteByName } from '../../router/routes';
import history from '../../store/history';
import { fillRoutePath } from '../../helpers/path';
import { BidOption } from '../../store/bid/types';
import { getReviewer, stringifyOrganization } from '../../helpers/users';
import { buildDTO } from '../../store/review/utils';
import tableSlice from '../../store/table/slice';
import { Assignment, UpdateAssignment } from '../../store/review/types';
import assignmentSlice from '../../store/review/slice';
import useFetchTableData from '../../store/table/useFetchTableData';

interface Props {
  getSubmissionsAction: () => void;
  submissionsById: { [key: string]: Submission };
  loading: boolean;
  keywordById: { [key: number]: Keyword };
  info: Info;
  roleById: { [key: string]: Role };
  bidOptionsById: { [key: number]: BidOption };
  clearNotifiablesAction: () => void;
  updateAssignmentAction: (data: UpdateAssignment) => void;
  deleteInfoAction: (data: InfoDeleteDTO) => void;
}

const PapersToReviewersDetailPage = ({
  getSubmissionsAction,
  submissionsById,
  loading,
  keywordById,
  info,
  roleById,
  bidOptionsById,
  clearNotifiablesAction,
  updateAssignmentAction,
  deleteInfoAction,
}: Props) => {
  const { t, i18n } = useTranslation();
  const params: any = useParams();
  const submissionExternalId = parseInt(params.submissionId);
  const roleId = parseInt(params.roleId);

  const friendlyName: TableFriendlyName = 'submission_assignments';
  const queryParams = { assignable_id: submissionExternalId, target_role_id: roleId };

  const [fetchData, fetchCustomColumnsData, getInfoAction] = useFetchTableData(friendlyName, queryParams);

  /* Fetch table data */
  useEffect(() => {
    if (Object.keys(submissionsById).length === 0) {
      getSubmissionsAction();
    }
    return () => {
      clearNotifiablesAction(); // Clear notifications at page exit
      deleteInfoAction({ friendlyName });
    };
  }, []);

  // Given id must match with external_id field
  const submission = Object.values(submissionsById).find(
    (submission) => submission.external_id == submissionExternalId,
  );

  if (loading) {
    return <Loading />;
  } else if (!submission) {
    return <div>Submission #{submissionExternalId} not found</div>;
  }

  /* Generate data for table */
  const chairInfo = info as ChairInfo;
  const inputData: unknown[] = [];
  const assignmentData = Object.values(info[friendlyName].byId) as Assignment[];
  assignmentData.forEach((additional) => {
    const reviewer = getReviewer(additional.person_role_id, chairInfo.users);
    if (reviewer) {
      const newRegister = {
        ...reviewer,
        assignment_id: additional.id,
        affinity: additional.affinity,
        bid: additional.bid_option_id ? bidOptionsById[additional.bid_option_id].name : undefined,
        conflict: !!additional.conflicts,
        conflicts: additional.conflicts,
        ignored: additional.ignored,
        assigned: additional.assigned,
        completed: additional.id ? additional.completed : undefined,
      };

      // Add Role description
      newRegister.role = roleById[reviewer.role_id].description;

      formatKeywordColumn(newRegister, keywordById);

      // @ts-ignore
      newRegister.organization = stringifyOrganization(newRegister);

      inputData.push(newRegister);
    }
  });

  const parentRoute = getRouteByName('RouteAssignmentPapersToReviewers');

  return (
    <div className="flex flex-col h-full">
      <TableHeader
        onBack={() => history.push(fillRoutePath(parentRoute.path))}
        onMove={(rowId) => {
          const submission = submissionsById[rowId];
          getInfoAction({ friendlyName, queryParams: { ...queryParams, assignable_id: submission.external_id } });
          history.replace(
            fillRoutePath(getRouteByName('RouteAssignmentsPapersToReviewersDetail').path, {
              submissionId: submission.external_id.toString(),
              roleId: roleId.toString(),
            }),
          );
        }}
        type={'App\\Models\\Submission'}
        row={submission}
        keywordById={keywordById}
        roleById={roleById}
      />
      <div className="mt-8" />
      <MyGridRedux
        friendlyName={friendlyName}
        inputData={inputData as Record<string, unknown>[]}
        updateAssignmentAction={(modelId, assigned, assignmentId, skipConflicts) => {
          const [dto, keyBy] = buildDTO(
            modelId,
            assigned,
            'assignable_id',
            submission?.id ?? 0,
            'App\\Models\\Submission',
            roleId,
          );
          updateAssignmentAction({
            ...dto,
            modelId,
            tableFriendlyName: friendlyName,
            queryParams,
            assignmentId,
            skipConflicts,
          });
        }}
        getCustomColumnsData={fetchCustomColumnsData}
      />
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectSubmissionState(state).loading || selectInfo(state).loading,
  submissionsById: selectSubmissionState(state).submissionsById,
  keywordById: selectKeywordState(state).keywordById,
  info: state.info.info,
  roleById: selectTrackRoles(state),
  bidOptionsById: selectBidState(state).bidOptions.byId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getSubmissionsAction: () => dispatch(submissionSlice.actions.GET_SUBMISSIONS()),
  deleteInfoAction: (data: InfoDeleteDTO) => dispatch(infoSlice.actions.DELETE(data)),
  clearNotifiablesAction: () => dispatch(tableSlice.actions['NOTIFIABLES:CLEAR']()),
  updateAssignmentAction: (data: UpdateAssignment) => dispatch(assignmentSlice.actions['ASSIGNMENTS:UPDATE'](data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PapersToReviewersDetailPage);
